import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ArrowLeft, Palette, ShoppingBag, Sparkles, Users, TrendingUp, XCircle } from 'lucide-react';

export default function TaskManagement({ user, onLogout }) {
  const navigate = useNavigate();
  const [accessDeniedDialog, setAccessDeniedDialog] = useState(false);
  const [deniedModule, setDeniedModule] = useState('');

  const taskModules = [
    {
      title: 'Design & Sampling',
      description: 'Design creation, approval workflow, and sampling tracking',
      icon: Palette,
      path: '/task-management/design-sampling',
      permission: 'task_design_sampling'
    },
    {
      title: 'Merchandise',
      description: 'Product merchandising, planning, and assortment management',
      icon: ShoppingBag,
      path: '/task-management/merchandise',
      permission: 'task_merchandise'
    },
    {
      title: 'Digital Design',
      description: 'Digital design creation, editing, and approval',
      icon: Sparkles,
      path: '/task-management/digital-design',
      permission: 'task_digital_design'
    },
    {
      title: 'Meeting Minutes',
      description: 'Create and manage meeting minutes with participants',
      icon: Users,
      path: '/task-management/meeting-minutes',
      permission: 'task_meeting_minutes'
    },
    {
      title: 'Executive Briefing Report',
      description: 'Executive reports and briefings for Merchandise, Fashion Design, and Textile Design',
      icon: TrendingUp,
      path: '/task-management/executive-briefing',
      permission: 'task_weekly_progress'
    }
  ];

  // Handle module click with permission check
  const handleModuleClick = (moduleName, path, requiredPermission) => {
    if (user && user[requiredPermission]) {
      navigate(path);
    } else {
      setDeniedModule(moduleName);
      setAccessDeniedDialog(true);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="hover:bg-white"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-4xl font-bold">Task Management</h1>
              <p className="text-gray-600 mt-2">Task creation, assignment, tracking, and reporting</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={onLogout}
            className="border-2 border-gray-300 hover:border-black hover:bg-black hover:text-white"
          >
            Logout
          </Button>
        </div>

        {/* Module Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {taskModules.map((module, idx) => {
            const Icon = module.icon;
            return (
              <Card
                key={idx}
                className="hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-black"
                onClick={() => handleModuleClick(module.title, module.path, module.permission)}
              >
                <CardHeader className="text-center">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-lg bg-black flex items-center justify-center">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {module.title}
                  </CardTitle>
                  <CardDescription className="text-sm mt-2">
                    {module.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Access Denied Dialog */}
      <Dialog open={accessDeniedDialog} onOpenChange={setAccessDeniedDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl font-bold">Access Denied</DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              You do not have permission to access the {deniedModule} module.
              <br />
              <br />
              Please contact your administrator to request access.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              onClick={() => setAccessDeniedDialog(false)}
              className="bg-black hover:bg-gray-800 text-white px-8"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
